class SapOfferTab extends HTMLElement {
  connectedCallback() {
    if (this.events) return;
    this.hidden = true;
    this.events = new AbortController();
    this.forms = new Set();
    this.dismissed = false;
    this.pending = false;
    this.opener = this.querySelector('[data-offer-open]');
    this.closer = this.querySelector('[data-offer-close]');
    this.sessionKey = 'sap-offer-tab-dismissed';
    try { this.dismissed = sessionStorage.getItem(this.sessionKey) === 'true'; } catch {}
    const on = (target, name, handler) => target.addEventListener(name, handler, { signal: this.events.signal });
    on(this.closer, 'click', () => this.dismiss());
    on(this.opener, 'click', () => this.open());
    on(document, 'focusin', event => { if (!this.contains(event.target)) this.lastFocus = event.target; });
    on(window, 'klaviyoForms', event => {
      const detail = event.detail || {};
      const form = detail.formId || detail.formID || 'popup';
      if (detail.type === 'open') {
        this.forms.add(form);
        this.pending = false;
        clearTimeout(this.pendingTimer);
      } else if (detail.type === 'close') {
        this.forms.delete(form);
        this.pending = false;
        clearTimeout(this.pendingTimer);
      } else if (detail.type === 'submit') this.dismiss();
      this.schedule();
    });
    on(window, 'scroll', () => this.schedule());
    on(window, 'resize', () => this.schedule());
    on(window, 'pageshow', () => this.schedule());
    on(window, 'popstate', () => this.schedule());
    this.observer = new MutationObserver(() => this.schedule());
    this.observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'open', 'aria-hidden'] });
    this.revealTimer = setTimeout(() => {
      this.hidden = false;
      this.sync();
    }, 3000);
  }

  disconnectedCallback() {
    this.events?.abort();
    this.events = null;
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
    this.frame = null;
    clearTimeout(this.revealTimer);
    clearTimeout(this.pendingTimer);
  }

  schedule() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => { this.frame = null; this.sync(); });
  }

  dismiss() {
    const hadFocus = this.contains(document.activeElement);
    this.dismissed = true;
    try { sessionStorage.setItem(this.sessionKey, 'true'); } catch {}
    this.sync();
    if (!hadFocus) return;
    // The Close button hides with the tab, so hand focus back to where the shopper was, or to the main content (A11y A5).
    let target = this.lastFocus?.isConnected && this.lastFocus !== document.body && this.lastFocus.checkVisibility?.({ visibilityProperty: true }) ? this.lastFocus : document.getElementById('MainContent');
    if (!target) return;
    if (target.id === 'MainContent' && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }

  sync() {
    const blockedRoute = /(?:^|\/)(?:cart|checkouts?|account)(?:\/|$)/.test(location.pathname);
    const overlay = document.querySelector('dialog[open], cart-drawer.is-opened, [data-sap-kit-header].is-open');
    const existingPopup = [...document.querySelectorAll('[role="dialog"][aria-label="POPUP Form"]')].some(dialog => dialog.checkVisibility({ opacityProperty: true, visibilityProperty: true }));
    const lockedBody = ['klaviyo-prevent-body-scrolling', 'scroll-y-off', 'scroll-y-off-firefox'].some(name => document.body.classList.contains(name));
    const bounds = this.getBoundingClientRect();
    const entryOffset = new DOMMatrixReadOnly(getComputedStyle(this).transform).m41;
    const collision = [...document.querySelectorAll('.sap-sticky-atc.is-visible, [data-pp-gallery-zoom], [data-pp-gallery-step], .sap-pp-video__toggle, [data-shop-filter-open], .sap-search-scope [data-scope-clear], .sap-search-scope input[type="search"], .sap-search-scope button[type="submit"]')].some(control => {
      if (!control.checkVisibility({ opacityProperty: true, visibilityProperty: true })) return false;
      const other = control.getBoundingClientRect();
      return other.width > 0 && other.height > 0 && other.left < bounds.right - entryOffset && other.right > bounds.left - entryOffset && other.top < bounds.bottom && other.bottom > bounds.top;
    });
    this.toggleAttribute('data-suppressed', this.dismissed || this.pending || this.forms.size > 0 || blockedRoute || !!overlay || existingPopup || lockedBody || collision);
  }

  open() {
    if (this.hasAttribute('data-suppressed')) return;
    this.pending = true;
    this.sync();
    this.pendingTimer = setTimeout(() => { this.pending = false; this.sync(); }, 5000);
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['openForm', this.mobileDevice() ? 'Yu5Kpu' : 'XCTEuW']);
  }

  mobileDevice() {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera || '';
    const mobileAgent = /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i;
    const mobilePrefix = /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i;
    return !!window.klaviyoForceMobile || mobileAgent.test(userAgent) || mobilePrefix.test(userAgent.slice(0, 4));
  }
}

if (!customElements.get('sap-offer-tab')) customElements.define('sap-offer-tab', SapOfferTab);
